import { useEffect, useRef, useState, FormEvent } from 'react'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const LOGO_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('velink_token')}`,
    'Content-Type': 'application/json',
  }
}

interface CFProfile {
  id: string
  name: string
  email: string
  siret: string | null
  phone: string | null
  isQualiopi: boolean
  qualiopiDoc: string | null
  logoUrl: string | null
}

export default function CFParametresPage() {
  const [profile, setProfile] = useState<CFProfile | null>(null)
  const [profileMsg, setProfileMsg] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [pwdMsg, setPwdMsg] = useState('')
  const [pwdLoading, setPwdLoading] = useState(false)

  // Qualiopi local state
  const [qualiopiEnabled, setQualiopiEnabled] = useState(false)
  const [qualiopiDoc, setQualiopiDoc] = useState('')
  const [qualiopiMsg, setQualiopiMsg] = useState('')
  const [qualiopiLoading, setQualiopiLoading] = useState(false)
  const [qualiopiError, setQualiopiError] = useState('')

  // Logo state
  const logoRef = useRef<HTMLInputElement>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoMsg, setLogoMsg] = useState('')

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data: CFProfile) => {
        setProfile(data)
        setQualiopiEnabled(data.isQualiopi)
        setQualiopiDoc(data.qualiopiDoc ?? '')
        if (data.logoUrl) {
          const [companyId, filename] = data.logoUrl.split('/')
          fetch(`${API}/api/companies/logo/${companyId}/${filename}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
          })
            .then((r) => r.ok ? r.blob() : null)
            .then((blob) => blob ? setLogoPreview(URL.createObjectURL(blob)) : null)
            .catch(() => {})
        }
      })
      .catch(() => {})
  }, [])

  async function uploadLogo(file: File) {
    if (!LOGO_MIME.includes(file.type)) { setLogoMsg('Format non autorisé (JPG, PNG, WebP uniquement)'); return }
    setLogoUploading(true)
    setLogoMsg('')
    setLogoPreview(URL.createObjectURL(file))
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/api/companies/me/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
        body: fd,
      })
      if (!res.ok) throw new Error('Erreur lors de l\'upload')
      const data = await res.json()
      setProfile((p) => p ? { ...p, logoUrl: data.logoUrl } : p)
      setLogoMsg('Logo mis à jour ✓')
    } catch (e: unknown) {
      setLogoMsg(e instanceof Error ? e.message : 'Erreur')
      setLogoPreview(null)
    } finally {
      setLogoUploading(false)
    }
  }

  async function saveProfile(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setProfileMsg('')
    setProfileLoading(true)
    const fd = new FormData(e.currentTarget)
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          name: fd.get('name'),
          siret: fd.get('siret') || null,
          phone: fd.get('phone') || null,
        }),
      })
      if (!res.ok) throw new Error()
      setProfileMsg('Profil mis à jour.')
      setTimeout(() => setProfileMsg(''), 3000)
    } catch {
      setProfileMsg('Erreur lors de la mise à jour.')
    } finally {
      setProfileLoading(false)
    }
  }

  async function saveQualiopi() {
    setQualiopiError('')
    if (qualiopiEnabled && (!qualiopiDoc || qualiopiDoc.trim().length < 3)) {
      setQualiopiError('Le justificatif Qualiopi est obligatoire pour activer la certification.')
      return
    }
    setQualiopiLoading(true)
    setQualiopiMsg('')
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          isQualiopi: qualiopiEnabled,
          qualiopiDoc: qualiopiEnabled ? qualiopiDoc : null,
        }),
      })
      if (!res.ok) throw new Error()
      setProfile((prev) => prev ? { ...prev, isQualiopi: qualiopiEnabled, qualiopiDoc: qualiopiEnabled ? qualiopiDoc : null } : prev)
      setQualiopiMsg('Statut Qualiopi mis à jour.')
      setTimeout(() => setQualiopiMsg(''), 3000)
    } catch {
      setQualiopiMsg('Erreur lors de la mise à jour.')
    } finally {
      setQualiopiLoading(false)
    }
  }

  async function changePwd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setPwdMsg('')
    setPwdLoading(true)
    const fd = new FormData(e.currentTarget)
    const newPwd = fd.get('newPassword') as string
    const confirm = fd.get('confirmPassword') as string
    if (newPwd !== confirm) { setPwdMsg('Les mots de passe ne correspondent pas.'); setPwdLoading(false); return }
    try {
      const res = await fetch(`${API}/api/auth/password`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ currentPassword: fd.get('currentPassword'), newPassword: newPwd }),
      })
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message ?? 'Erreur') }
      setPwdMsg('Mot de passe modifié.')
      ;(e.target as HTMLFormElement).reset()
      setTimeout(() => setPwdMsg(''), 3000)
    } catch (err: unknown) {
      setPwdMsg(err instanceof Error ? err.message : 'Erreur')
    } finally {
      setPwdLoading(false)
    }
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="p-8 max-w-2xl">
      <h1 className="text-2xl font-bold text-text-primary mb-8">Paramètres</h1>

      {/* Statut Qualiopi */}
      <section className="glass-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
              <span className="text-lg">🏅</span> Certification Qualiopi
            </h2>
            <p className="text-text-muted text-xs mt-0.5">Label qualité reconnu par l'État pour les organismes de formation</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${profile.isQualiopi ? 'bg-success/10 border-success/25 text-success' : 'bg-white/[0.06] border-white/[0.10] text-text-muted'}`}>
            {profile.isQualiopi ? '✓ Certifié Qualiopi' : 'Non certifié'}
          </span>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setQualiopiEnabled((v) => !v)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${qualiopiEnabled ? 'bg-accent' : 'bg-white/[0.12]'}`}
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${qualiopiEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-text-secondary">{qualiopiEnabled ? 'Qualiopi activé' : 'Qualiopi désactivé'}</span>
          </label>

          {qualiopiEnabled && (
            <div>
              <label className="glass-label">
                Justificatif <span className="text-danger">*</span>
                <span className="text-text-muted font-normal ml-1">(numéro de certificat ou organisme certificateur)</span>
              </label>
              <input
                type="text"
                value={qualiopiDoc}
                onChange={(e) => setQualiopiDoc(e.target.value)}
                placeholder="ex. Bureau Veritas — n°QUA-2024-0001"
                className={`glass-input${qualiopiError ? ' error' : ''}`}
              />
              {qualiopiError && <p className="glass-field-error">{qualiopiError}</p>}
            </div>
          )}

          {qualiopiMsg && (
            <p className={`text-sm ${qualiopiMsg.includes('Erreur') ? 'text-danger' : 'text-success'}`}>{qualiopiMsg}</p>
          )}

          <button
            onClick={saveQualiopi}
            disabled={qualiopiLoading}
            className="glass-btn-accent px-5 py-2 text-sm rounded-xl"
          >
            {qualiopiLoading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </section>

      {/* Logo */}
      <section className="glass-card p-6 mb-6">
        <h2 className="text-base font-semibold text-text-primary mb-5">Logo du centre de formation</h2>
        <div className="flex items-center gap-5">
          <div
            onClick={() => logoRef.current?.click()}
            className="relative w-20 h-20 rounded-2xl overflow-hidden cursor-pointer group flex-shrink-0 border border-white/10 hover:border-accent/40 transition-colors"
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-white/[0.06] flex items-center justify-center text-2xl font-bold text-text-muted">
                {profile.name?.charAt(0).toUpperCase() ?? '?'}
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            {logoUploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>
          <input
            ref={logoRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f) }}
          />
          <div>
            <p className="text-text-secondary text-sm">Cliquez sur le logo pour le modifier.</p>
            <p className="text-text-muted text-xs mt-1">JPG, PNG, WebP — carré recommandé, max 5 Mo</p>
            {logoMsg && (
              <p className={`text-xs mt-2 ${logoMsg.includes('✓') ? 'text-success' : 'text-danger'}`}>{logoMsg}</p>
            )}
          </div>
        </div>
      </section>

      {/* Profil */}
      <section className="glass-card p-6 mb-6">
        <h2 className="text-base font-semibold text-text-primary mb-4">Informations du centre</h2>
        <form onSubmit={saveProfile} className="space-y-4">
          <div>
            <label className="glass-label">Nom du centre</label>
            <input type="text" name="name" defaultValue={profile.name} className="glass-input" required />
          </div>
          <div>
            <label className="glass-label">SIRET / NDA <span className="text-text-muted font-normal">(optionnel)</span></label>
            <input type="text" name="siret" defaultValue={profile.siret ?? ''} className="glass-input" maxLength={14} />
          </div>
          <div>
            <label className="glass-label">Téléphone <span className="text-text-muted font-normal">(optionnel)</span></label>
            <input type="tel" name="phone" defaultValue={profile.phone ?? ''} className="glass-input" />
          </div>
          {profileMsg && <p className={`text-sm ${profileMsg.includes('Erreur') ? 'text-danger' : 'text-success'}`}>{profileMsg}</p>}
          <button type="submit" disabled={profileLoading} className="glass-btn-accent px-5 py-2 text-sm rounded-xl">
            {profileLoading ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </button>
        </form>
      </section>

      {/* Mot de passe */}
      <section className="glass-card p-6">
        <h2 className="text-base font-semibold text-text-primary mb-4">Changer le mot de passe</h2>
        <form onSubmit={changePwd} className="space-y-4">
          <div>
            <label className="glass-label">Mot de passe actuel</label>
            <input type="password" name="currentPassword" className="glass-input" required />
          </div>
          <div>
            <label className="glass-label">Nouveau mot de passe</label>
            <input type="password" name="newPassword" className="glass-input" required minLength={8} />
          </div>
          <div>
            <label className="glass-label">Confirmer le nouveau mot de passe</label>
            <input type="password" name="confirmPassword" className="glass-input" required />
          </div>
          {pwdMsg && <p className={`text-sm ${pwdMsg.includes('Erreur') || pwdMsg.includes('correspondent') ? 'text-danger' : 'text-success'}`}>{pwdMsg}</p>}
          <button type="submit" disabled={pwdLoading} className="glass-btn-accent px-5 py-2 text-sm rounded-xl">
            {pwdLoading ? 'Modification…' : 'Modifier le mot de passe'}
          </button>
        </form>
      </section>
    </div>
  )
}
